import { UserScope } from '@/models/users'
import { RouteConfig } from 'vue-router'

// pages components
import Login from '@/views/Auth/Login.vue'
import Recovery from '@/views/Auth/Recovery.vue'
import Confirm from '@/views/Auth/Confirm.vue'
import Register from '@/views/Auth/Register.vue'
import InvalidLink from '@/views/Auth/InvalidLink.vue'
import CompanyCreate from '@/views/Company/CompanyCreate.vue'
import Dashboard from '@/views/Dashboard.vue'
import Analytic from '@/views/Analytic/Analytic.vue'
import Records from '@/views/Record/Records.vue'
import AccessBlocked from '@/views/Errors/AccessBlocked.vue'
import Error404 from '@/views/Errors/Error404.vue'
import Billing from '@/views/Billing/Billing.vue'

export const enum RouteNames {
  App = 'app',
  Login = 'login',
  Registration = 'registration',
  InvalidLink = 'invalid-link',
  ActiveScript = 'active-script',
  ScriptFinish = 'script-finish',
  ScriptPage = 'script-page',
  ScriptEdit = 'script-edit',
  ScriptSchemePreview = 'script-scheme-preview',
  ScriptSchemeEdit = 'script-scheme-edit',
  ScriptsPage = 'scripts',
  ScriptTemplates = 'script-templates',
  Records = 'records',
  VersionCreate = 'version-create',
  ScriptOption = 'script-option',
  UserSettings = 'user-settings',
  UserCredentials = 'user-credentials',
  CompanySettings = 'company-settings',
  CompanyCreate = 'company-create',
  Knowledge = 'knowledge',
  KnowledgeSection = 'knowledge-section',
  Rating = 'rating',
  BackwardReloader = 'backward-reloader',
  AccessBlocked = 'access-blocked',
}

interface CustomRouteConfig<T> extends RouteConfig {
  meta?: T
}

interface RouteMetaData {
  auth?: boolean
  scope?: UserScope
  company?: boolean
  backward?: boolean
}

const routes: Array<CustomRouteConfig<RouteMetaData>> = [
  {
    path: '/',
    name: RouteNames.App,
    redirect: '/scripts',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
  },
  {
    path: '/scripts',
    name: RouteNames.ScriptsPage,
    component: () =>
      import(/* webpackChunkName: "scripts" */ '@/views/Script/Scripts.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsView,
      backward: true,
    },
  },
  {
    path: '/scripts/templates',
    name: RouteNames.ScriptTemplates,
    component: () => import('@/views/ScriptTemplates/ScriptTemplates.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
      backward: true,
    },
  },
  {
    path: '/scripts/create',
    name: 'script-create',
    component: () =>
      import(
        /* webpackChunkName: "scripts" */ '@/views/Script/ScriptsCreate.vue'
      ),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
      backward: true,
    },
  },
  {
    path: `/scripts/active`,
    name: RouteNames.ActiveScript,
    component: () =>
      import(
        /* webpackChunkName: "scripts" */ '@/views/Script/ActiveScript.vue'
      ),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsUse,
    },
  },
  {
    path: `/scripts/finish`,
    name: RouteNames.ScriptFinish,
    component: () =>
      import(
        /* webpackChunkName: "scripts" */ '@/views/Script/FinishScript.vue'
      ),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsUse,
    },
  },
  {
    path: `/scripts/:scriptId(\\d+)/edit/:instanceId(\\d+)/cards/:cardId(\\d+)`,
    name: RouteNames.ScriptEdit,
    component: () =>
      import(/* webpackChunkName: "scripts" */ '@/views/Script/ScriptEdit.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
    },
  },
  {
    path: `/scripts/:scriptId/instance/:instanceId/cards/:cardId(\\d+)/:variant(a|b)/options/:optionId?`,
    name: RouteNames.ScriptOption,
    component: () => import('@/views/Script/ScriptOption.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
    },
  },
  {
    path: `/scripts/:scriptId/scheme/preview/:instanceId?`,
    name: RouteNames.ScriptSchemePreview,
    component: () => import('@/views/Script/Scheme.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
    },
  },
  {
    path: `/scripts/:scriptId/edit/:instanceId/scheme`,
    name: RouteNames.ScriptSchemeEdit,
    component: () => import('@/views/Script/Scheme.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
    },
  },
  {
    path: `/scripts/:scriptId/versions/create`,
    name: RouteNames.VersionCreate,
    component: () => import('@/views/Script/VersionCreate.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsEdit,
    },
  },
  {
    path: `/scripts/:scriptId/preview/:instanceId?`,
    name: RouteNames.ScriptPage,
    component: () =>
      import(/* webpackChunkName: "scripts" */ '@/views/Script/Script.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.ScriptsView,
    },
  },
  {
    path: '/invitation/:key',
    name: 'invitation',
    component: () => import('@/views/Auth/Invitation.vue'),
  },
  {
    path: '/login',
    name: RouteNames.Login,
    component: Login,
  },
  {
    path: '/recovery/:code?',
    name: 'recovery',
    component: Recovery,
  },
  {
    path: '/invalid-link',
    name: RouteNames.InvalidLink,
    component: InvalidLink,
  },
  {
    path: '/registration/:key',
    name: 'confirm',
    component: Confirm,
  },
  {
    path: '/registration',
    name: RouteNames.Registration,
    component: Register,
  },
  {
    path: '/company/create',
    name: RouteNames.CompanyCreate,
    component: CompanyCreate,
    meta: {
      auth: true,
    },
  },
  {
    path: '/records',
    name: RouteNames.Records,
    component: Records,
    meta: {
      auth: true,
      scope: UserScope.Stats,
      backward: true,
    },
  },
  // {
  //   path: '/billing',
  //   name: 'billing',
  //   component: Billing,
  //   meta: {
  //     auth: true,
  //     scope: UserScope.Finance,
  //     backward: true,
  //   },
  // },
  {
    path: '/analytic',
    name: 'analytic',
    component: Analytic,
    meta: {
      company: true,
      scope: UserScope.Stats,
    },
  },
  {
    path: '/knowledge/:id?',
    name: RouteNames.Knowledge,
    component: () => import('@/views/Knowledge/Knowledge.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.KnowledgeEdit,
    },
  },
  {
    path: '/knowledge/:baseId/section',
    name: RouteNames.KnowledgeSection,
    component: () => import('@/views/Knowledge/KnowledgeSection.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.KnowledgeEdit,
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () =>
      import(/* webpackChunkName: "settings" */ '@/views/User/Users.vue'),
    meta: {
      company: true,
      auth: true,
      backward: true,
    },
  },
  {
    path: '/users/invite',
    name: 'users-invite',
    component: () =>
      import(/* webpackChunkName: "settings" */ '@/views/User/Invite.vue'),
    meta: {
      auth: true,
      scope: UserScope.Users,
      backward: true,
    },
  },
  {
    path: '/user/settings',
    name: RouteNames.UserSettings,
    component: () =>
      import(/* webpackChunkName: "settings" */ '@/views/User/Settings.vue'),
    meta: {
      auth: true,
      backward: true,
    },
  },
  {
    path: '/company/settings',
    name: RouteNames.CompanySettings,
    component: () =>
      import(/* webpackChunkName: "settings" */ '@/views/Company/Settings.vue'),
    meta: {
      auth: true,
      company: true,
      scope: UserScope.Core,
      backward: true,
    },
  },
  {
    path: '/user/settings/password',
    name: RouteNames.UserCredentials,
    component: () =>
      import(
        /* webpackChunkName: "settings" */ '@/views/User/SettingsPassword.vue'
      ),
    meta: {
      auth: true,
    },
  },
  {
    path: '/integration',
    name: 'integrations',
    component: () => import('@/views/Integration/Integrations.vue'),
    meta: {
      auth: true,
      backward: true,
    },
  },
  {
    path: '/rating/:score(1|2|3|4|5)',
    name: RouteNames.Rating,
    component: () => import('@/views/Rating/Rating.vue'),
  },
  {
    path: '/reloader',
    name: RouteNames.BackwardReloader,
    component: () => import('@/views/BackwardReloader/BackwardReloader.vue'),
  },
  {
    path: '/access-blocked',
    name: RouteNames.AccessBlocked,
    component: AccessBlocked,
  },
  {
    path: '/404',
    name: 'error404',
    component: Error404,
  },
  {
    path: '/*',
    redirect: '/404',
  },
]

export default routes